import React, { useState } from "react";
import { useRouter } from "next/router";
import { useQuery } from "@tanstack/react-query";
import MainLayout from "../../components/layout/MainLayout";
import { ProductItem } from "../../components/common/ProductItem.comp";
import { Product, ProductFilterFormData } from "../../types/product";
import { Pagination } from "../../components/pagination.comp";
import { ItemsPerPageSelector } from "../../components/itemsPerPage.comp";
import productCore from "../../core/product.core";
import { LoadingComp } from "../../components/common/loading.comp";
import ArticleBanner from "../../components/articleBanner.comp";
import { useTranslation } from "../../core/translation.core";

export default function CharityPage() {
  return (
    <MainLayout title="義賣助毛孩" url="/article/charity">
      <Main />
    </MainLayout>
  );
}

function Main() {
  const { t, msg } = useTranslation();
  const router = useRouter();
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [wishlistedItems, setWishlistedItems] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(18);

  // Fetch products using React Query
  const {
    data: productsData,
    isPending,
    isError,
    error,
  } = useQuery({
    queryKey: ["productListByCatSlug", "charity", currentPage, itemsPerPage, router.locale],
    queryFn: () =>
      productCore.filter(
        {
          catSlug: "義賣助毛孩",
          page: currentPage,
          size: itemsPerPage,
        } as ProductFilterFormData,
        router.locale
      ),
    enabled: router.isReady,
  });

  // Clear success message after 3 seconds
  React.useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage("");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Function to update wishlist items
  const updateWishlistItem = (productName: string, wishlistStatus: boolean) => {
    const newWishlisted = new Set(wishlistedItems);
    if (wishlistStatus) {
      newWishlisted.add(productName);
      setSuccessMessage(`已加入"${productName}"到願望清單`);
    } else {
      newWishlisted.delete(productName);
      setSuccessMessage(`已從願望清單移除"${productName}"`);
    }
    setWishlistedItems(newWishlisted);
  };

  // Handle items per page change
  const handleItemsPerPageChange = (value: number) => {
    setItemsPerPage(value);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  // Get products and total from query data
  const products = productsData?.list || [];
  const totalItems = productsData?.size ?? 0;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  // Calculate pagination display info
  const startIdx = (currentPage - 1) * itemsPerPage;
  const endItem = Math.min(startIdx + itemsPerPage, totalItems);
  const startItem = totalItems > 0 ? startIdx + 1 : 0;

  return (
    <div className="min-h-screen bg-guru-floral-white mt-6">
      <ArticleBanner title="義賣助毛孩" />

      {successMessage && (
        <div className="fixed top-20 right-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded z-50">
          {successMessage}
        </div>
      )}

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        {/* <h2 className="text-xl md:text-2xl font-semibold mb-6">Charity Products</h2> */}

        {isPending ? (
          <LoadingComp padding="py-8" />
        ) : isError ? (
          <div className="text-center py-4 text-red-500">Error: {error.message}</div>
        ) : products && products.length > 0 ? (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 px-2">
              {products.map((product: Product) => (
                <div className="flex justify-center" key={product.id}>
                  <ProductItem
                    key={product.id}
                    product={product}
                    wishList={wishlistedItems.has(product.id)}
                    updateItem={updateWishlistItem}
                    showDiscount={true}
                  />
                </div>
              ))}
            </div>

            {/* Pagination and Items Per Page Section */}
            <div className="mt-10 mb-6">
              <div className="flex flex-col md:flex-row justify-between items-center">
                <div className="mb-4 md:mb-0">
                  <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
                </div>

                <div className="hidden md:block">
                  <ItemsPerPageSelector
                    itemsPerPage={itemsPerPage}
                    onItemsPerPageChange={handleItemsPerPageChange}
                    options={[12, 18, 24]}
                    totalItems={totalItems}
                    startItem={startItem}
                    endItem={endItem}
                  />
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-4 text-gray-500">{t(msg.product.noProductsAvailable)}</div>
        )}
      </div>
    </div>
  );
}
